<template>
    <dashboard>
        <row>
            <panel size="col-5" class="filters">
                <h3 slot="title">Filters</h3>
                <label class="filter-item">
                    <h4>Date Range</h4>
                    <app-date-picker
                        id="dateRange"
                        :value="_dateRange"
                        type="daterange"
                        size="small"
                        class="no-icon"
                        @input="dateChanged"/>
                </label>
                <div class="filter-item">
                    <h4>Mode</h4>
                    <app-segment-control
                        :value="mode"
                        :options="[
                            { value: 'all', title: 'All' },
                            { value: 'issues', title: 'Issues' },
                            { value: 'absent', title: 'Absent' }
                        ]"
                        size="tight"
                        @input="modeChanged"/>
                </div>
                <div class="filter-item">
                    <h4>Teams</h4>
                    <app-tag
                        v-for="team in __teams"
                        :key="team.id"
                        size="small"
                        closable
                        @close="removeTeam(team)">{{ team.name }}</app-tag>
                    <app-button @click="showTeamPicker = true">Pick Team</app-button>
                </div>
                <div class="filter-item">
                    <h4>Courses</h4>
                    <app-tag
                        v-for="course in __courses"
                        :key="course.id"
                        size="small"
                        closable
                        @close="removeCourse(course)">{{ course.title }}</app-tag>
                    <app-button @click="showCoursePicker = true">Pick Course</app-button>
                </div>
                <div class="filter-item">
                    <h4>Events</h4>
                    <app-tag
                        v-for="event in __events"
                        :key="event.id"
                        size="small"
                        closable
                        @close="removeEvent(event)">{{ event.title }}</app-tag>
                    <app-button @click="showEventPicker = true">Pick Event</app-button>
                </div>

                <event-picker 
                    v-model="showEventPicker" 
                    state="all"
                    @selected-event="addEvent">
                    <template slot="title">Select Event</template>
                </event-picker>
                <course-picker 
                    v-model="showCoursePicker"
                    state="all"
                    @selected-class="addCourse">
                    <template slot="title">Select Course</template>
                </course-picker>
                <team-picker v-model="showTeamPicker" @selected-team="addTeam">
                    <template slot="title">Select Team</template>
                </team-picker>
            </panel>
            <panel size="col-19">
                <h2 slot="title">Status Report</h2>
                <template slot="buttons">
                    <app-button
                        size="small"
                        icon="icon-download"
                        class="no-text"
                        @click="handleDownload">
                        <span class="visuallyhidden">Download Report</span>
                    </app-button>
                </template>

                <el-table
                    :data="report"
                    :default-sort="{ prop: sort_by }"
                    style="width: 100%"
                    empty-text="None"
                    @sort-change="sortChange"
                    @current-change="navigateToStudentDetail">
                    <el-table-column
                        prop="student.first_name"
                        label="First Name"
                        sortable="custom"/>
                    <el-table-column prop="student.last_name" label="Last Name" sortable="custom"/>
                    <el-table-column prop="created_at" 
                                     label="Date" 
                                     width="125px" 
                                     sortable="custom">
                        <template
                            slot-scope="scope">{{ scope.row.created_at | moment("MM/DD/YYYY") }}</template>
                    </el-table-column>
                    <el-table-column prop="attendable.title" label="Course / Event" width="150px"/>
                    <el-table-column
                        v-if="$role.isAdmin() || $role.isAdvisor() || $role.isCoach()"
                        prop="student.teams"
                        label="Teams"
                        width="150px">
                        <template
                            slot-scope="scope">{{ scope.row.student.teams.map((item) => item.name).join(', ') }}</template>
                    </el-table-column>
                    <el-table-column
                        prop="status_name"
                        label="Status"
                        width="125px"
                        sortable="custom"/>
                </el-table>

                <pagination
                    :current-page="page"
                    :page-size="per_page"
                    :total="total"
                    layout="prev, pager, next, total"
                    @current-change="pageChange"/>
            </panel>
        </row>
    </dashboard>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import moment from 'moment'
import table from '@/mixins/table'
import download from '@/mixins/download'

import CoursePicker from '../Course/CoursePicker'
import TeamPicker from '../Team/TeamPicker'
import EventPicker from '../Event/EventPicker'

export default {
    name: 'page-reports-status',
    components: {
        CoursePicker,
        TeamPicker,
        EventPicker
    },
    mixins: [table, download],
    props: {
        start_date: {
            type: String,
            default: moment()
                .startOf('isoWeek')
                .format('YYYY-MM-DD')
        },
        end_date: {
            type: String,
            default: moment()
                .endOf('isoWeek')
                .format('YYYY-MM-DD')
        },
        per_page: {
            type: Number,
            default: 100
        },
        teams: String,
        courses: String,
        events: String,
        mode: {
            type: String,
            default: 'issues'
        }
    },
    data() {
        return {
            dateRange: null,
            showCoursePicker: false,
            showTeamPicker: false,
            showEventPicker: false,
            tableParamKeys: [
                'start_date',
                'end_date',
                'teams',
                'courses',
                'events',
                'mode'
            ]
        }
    },
    computed: {
        ...mapState({
            teamsList: ({ params }) => params.teams,
            report: ({ reportList }) => reportList.items,
            total: ({ reportList }) => reportList.total,
            authToken: ({ me }) => me.token
        }),
        ...mapGetters(['role']),
        _dateRange() {
            return [
                moment(this.start_date).toDate(), 
                moment(this.end_date).toDate()
            ]
        },
        __teams: {
            get() {
                return this.parseJson(this.teams)
            },
            set(newValue) {
                this.setJson('teams', newValue)
            }
        },
        __courses: {
            get() {
                return this.parseJson(this.courses)
            },
            set(newValue) {
                this.setJson('courses', newValue)
            }
        },
        __events: {
            get() {
                return this.parseJson(this.events)
            },
            set(newValue) {
                this.setJson('events', newValue)
            }
        }
    },
    mounted() {
        this.paramsChanged(this.getParameters())
    },
    methods: {
        ...mapActions(['getStatusReport']),
        handleDownload() {
            let params = this.getParameters()
            params.teams = this.parseJson(params.teams).map(item => item.id)
            params.courses = this.parseJson(params.courses).map(item => item.id)
            params.events = this.parseJson(params.events).map(item => item.id)
            this.postParameters('/api/v2/download/report/status',
                params,
                this.authToken)
        },
        navigateToStudentDetail({ student }) {
            this.$router.push({
                name: 'students.detail',
                params: { uuid: student.uuid }
            })
        },
        paramsChanged(params) {
            params.teams = this.parseJson(params.teams).map(item => item.id)
            params.courses = this.parseJson(params.courses).map(item => item.id)
            params.events = this.parseJson(params.events).map(item => item.id)
            this.getStatusReport(params)
        },
        modeChanged(mode) {
            var parameters = this.getParameters()
            parameters.mode = mode
            this.setParameters(parameters)
        },
        dateChanged(dateRange) {
            if (dateRange) {
                var parameters = this.getParameters()
                parameters.start_date = moment(dateRange[0]).format('YYYY-MM-DD')
                parameters.end_date = moment(dateRange[1]).format('YYYY-MM-DD')
                this.setParameters(parameters)
            }
        },

        addCourse({ id, title }) {
            var courses = this.__courses
            courses.push({ id, title })
            this.__courses = courses
        },
        removeCourse(course) {
            this.__courses = this.__courses.filter(item => item.id != course.id)
        },

        addEvent({ id, title }) {
            var events = this.__events
            events.push({ id, title })
            this.__events = events
        },
        removeEvent(event) {
            this.__events = this.__events.filter(item => item.id != event.id)
        },

        addTeam({ id, name }) {
            var teams = this.__teams
            teams.push({ id, name })
            this.__teams = teams
        },
        removeTeam(team) {
            this.__teams = this.__teams.filter(item => item.id != team.id)
        }
    }
}
</script>

<style lang="stylus">
.filters
    .app-date-picker
        width: 100% !important

    .el-date-editor.no-icon
        padding: 3px 10px !important

        .app-input__icon
            display: none

        .el-range-input
            width: 45%
</style>
